import Assign from 'core-js-pure/features/object/assign';
import { KustomerCoreWindow, KustomerCore } from 'core/types';
import { setupIframeForApi } from 'helpers/browser';
import { runQueue } from 'helpers/queue';
import { KustomerErrorResponse, PublicCallback } from 'globals/types';
import { KustomerEventTypes } from 'core/core_main/types';
import { SendMessageParameters } from 'core_main/sdk/send_message/types';
import { GetMessagesParameters } from 'core_main/sdk/get_messages/types';
import { MarkReadParameters } from 'core_main/sdk/mark_read/types';
import { GetConversationParameters } from 'core_main/sdk/get_conversation/types';
import { GetConversationsParameters } from 'core_main/sdk/get_conversations/types';
import { EndConversationParameters } from 'core_main/sdk/end_conversation/types';
import { SubmitSatisfactionFormParameters } from 'core_main/sdk/submit_satisfaction_form/types';
import { SendTypingActivityParameters } from 'core_main/sdk/send_typing_activity/types';
import { LoginParameters } from 'core_main/sdk/login/types';
import { IsLoggedInParameters } from 'core_main/sdk/is_logged_in/types';
import { DescribeCustomerParameters } from 'core_main/sdk/describe_customer/types';
import { DescribeConversationParameters } from 'core_main/sdk/describe_conversation/types';
import { CreateConversationParameters } from 'core_main/sdk/create_conversation/types';
import {
  GetArticlesCallbackResponse,
  GetArticlesParameters,
  GetArticleByIdParameters,
  ArticleCallbackResponse,
} from 'core_main/sdk/get_articles/types';
import { SearchArticlesParameters } from 'core_main/sdk/search_articles/types';
import { InitAssistantParameters } from 'core_main/api/assistant/types';
import {
  GetCategoriesCallbackResponse,
  GetCategoriesParameters,
} from 'core_main/sdk/get_categories/types';
import { IsUrlAllowedParameters } from 'core_main/sdk/is_url_allowed/types';
import { InitParameters } from 'core_main/sdk/init/types';
import { SendPresenceActivityParameters } from 'core_main/sdk/send_presence_activity/types';
import { GetAssistantParameters } from 'core_main/sdk/get_assistant/types';
import { GetKnowledgeBaseConfigCallbackResponse } from 'core_main/sdk/get_knowledge_base_config/types';
import { SubmitArticleFeedbackParameters } from 'core_main/sdk/submit_article_feedback/types';
import { ArticleFeedbackResponseData } from 'core_main/api/articles/types';
import { runMainMethod } from './helpers';

export const getKustomerCoreWindow = (): KustomerCoreWindow => window;
export const getKustomerCoreObject = (): KustomerCore | undefined => {
  const kustomerCoreWindow = getKustomerCoreWindow();

  if (!kustomerCoreWindow) return undefined;

  return kustomerCoreWindow.KustomerCore;
};

export const setupCore = () => {
  const kustomerCore = getKustomerCoreObject();

  if (!kustomerCore) return;

  Assign(kustomerCore, {
    init: (parameters?: InitParameters, cb?: PublicCallback) =>
      runMainMethod('init', parameters, cb),
    createConversation: (
      parameters: CreateConversationParameters,
      cb?: PublicCallback,
    ) => runMainMethod('createConversation', parameters, cb),
    addListener: (type: KustomerEventTypes, cb?: PublicCallback) =>
      runMainMethod('addListener', type, cb),
    removeListener: (type: KustomerEventTypes, cb?: PublicCallback) =>
      runMainMethod('removeListener', type, cb),
    sendMessage: (parameters: SendMessageParameters, cb?: PublicCallback) =>
      runMainMethod('sendMessage', parameters, cb),
    getConversations: (parameters: GetConversationsParameters, cb) =>
      runMainMethod('getConversations', parameters, cb),
    getConversation: (parameters: GetConversationParameters, cb) =>
      runMainMethod('getConversation', parameters, cb),
    getMessages: (parameters: GetMessagesParameters, cb) =>
      runMainMethod('getMessages', parameters, cb),
    getHistoricalMessages: (parameters: GetMessagesParameters, cb) =>
      runMainMethod('getHistoricalMessages', parameters, cb),
    getAssistant: (parameters: GetAssistantParameters) =>
      runMainMethod('getAssistant', parameters),
    markRead: (parameters: MarkReadParameters) =>
      runMainMethod('markRead', parameters),
    getUnreadCount: () => runMainMethod('getUnreadCount'),
    endConversation: (parameters: EndConversationParameters, cb) =>
      runMainMethod('endConversation', parameters, cb),
    initAssistant: (parameters: InitAssistantParameters, cb) =>
      runMainMethod('initAssistant', parameters, cb),
    isChatAvailable: () => runMainMethod('isChatAvailable'),
    getChatSettings: () => runMainMethod('getChatSettings'),
    submitSatisfactionForm: (
      parameters: SubmitSatisfactionFormParameters,
      cb,
    ) => {
      runMainMethod('submitSatisfactionForm', parameters, cb);
    },
    sendTypingActivity: (parameters: SendTypingActivityParameters) =>
      runMainMethod('sendTypingActivity', parameters),
    login: (parameters: LoginParameters, cb) =>
      runMainMethod('login', parameters, cb),
    isLoggedIn: (parameters: IsLoggedInParameters, cb) =>
      runMainMethod('isLoggedIn', parameters, cb),
    describeCustomer: (parameters: DescribeCustomerParameters, cb) =>
      runMainMethod('describeCustomer', parameters, cb),
    describeConversation: (parameters: DescribeConversationParameters, cb) =>
      runMainMethod('describeConversation', parameters, cb),
    getCategories: (
      parameters: GetCategoriesParameters,
      cb: (
        response: GetCategoriesCallbackResponse,
        error: KustomerErrorResponse,
      ) => void,
    ) => runMainMethod('getCategories', parameters, cb),
    getArticles: (
      parameters: GetArticlesParameters,
      cb: (
        response: GetArticlesCallbackResponse,
        error: KustomerErrorResponse,
      ) => void,
    ) => runMainMethod('getArticles', parameters, cb),
    getArticleById: (
      parameters: GetArticleByIdParameters,
      cb: (response: ArticleCallbackResponse) => void,
    ) => runMainMethod('getArticleById', parameters, cb),
    submitArticleFeedback: (
      parameters: SubmitArticleFeedbackParameters,
      cb: (response: ArticleFeedbackResponseData) => void,
    ) => runMainMethod('submitArticleFeedback', parameters, cb),
    getKnowledgeBaseConfig: (
      cb: (response: GetKnowledgeBaseConfigCallbackResponse) => void,
    ) => runMainMethod('getKnowledgeBaseConfig', cb),
    searchArticles: (parameters: SearchArticlesParameters, cb) =>
      runMainMethod('searchArticles', parameters, cb),
    isUrlAllowed: (parameters: IsUrlAllowedParameters) =>
      runMainMethod('isUrlAllowed', parameters),
    logout: (cb) => runMainMethod('logout', cb),
    sendPresenceActivity: (parameters: SendPresenceActivityParameters) =>
      runMainMethod('sendPresenceActivity', parameters),
  });
};

export const addMainReadyEventListener = (): void => {
  const kustomerCoreWindow = getKustomerCoreWindow();
  const kustomerCore = getKustomerCoreObject();

  kustomerCoreWindow.addEventListener('kustomer_core_main_ready', () => {
    if (!kustomerCore) return;

    setupCore();

    runQueue(kustomerCore);
  });
};

export const setupWindow = (): void => {
  addMainReadyEventListener();

  setupIframeForApi(
    'kustomer-core-sdk-iframe',
    'display: none',
    `<body>
      <script
        src="${process.env.KUSTOMER_CORE_MAIN_INCLUDE}">
      </script>
    </body>`,
    'Kustomer Core SDK Iframe',
  );
};

setupWindow();
